import { memo } from "react";
import { getBezierPath, type EdgeProps } from "reactflow";
import { motion } from "framer-motion";
import type { RoadmapEdgeData } from "./useRoadmapLayout";
import { STATUS_COLORS } from "./constants";

function AnimatedEdgeInner({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
}: EdgeProps<RoadmapEdgeData>) {
  const status = data?.targetStatus ?? "to_learn";
  const revealDelay = data?.revealDelay ?? 0;

  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
  });

  const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS]?.edge ?? STATUS_COLORS.to_learn.edge;
  const isNext = status === "next";
  const isKnown = status === "known";

  return (
    <g>
      {/* Invisible wider path for hit detection */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={12}
      />

      {/* Glow for "next" edges */}
      {isNext && (
        <motion.path
          d={edgePath}
          fill="none"
          stroke={color}
          strokeWidth={4}
          strokeOpacity={0.15}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.6, delay: revealDelay, ease: "easeOut" }}
        />
      )}

      {/* Main edge path */}
      <motion.path
        d={edgePath}
        fill="none"
        stroke={color}
        strokeWidth={isKnown ? 2 : 1.5}
        strokeDasharray={isKnown ? "none" : isNext ? "6 4" : "4 4"}
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        animate={{ pathLength: 1, opacity: 1 }}
        transition={{ duration: 0.5, delay: revealDelay, ease: "easeOut" }}
      />

      {/* Animated dash movement for "next" edges */}
      {isNext && (
        <path
          d={edgePath}
          fill="none"
          stroke={color}
          strokeWidth={1.5}
          strokeDasharray="6 4"
          strokeLinecap="round"
          style={{
            animation: "roadmapEdgeDash 1.5s linear infinite",
          }}
        />
      )}
    </g>
  );
}

export const AnimatedEdge = memo(AnimatedEdgeInner);
